import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { NodeProps, Node } from '@xyflow/react';
import type { DagNodeData } from './DagNodeComponent';
import type { WorkflowStepStatus } from '@/lib/types';
import { formatDurationMs } from '@/lib/format';
import { StatusIcon } from './StatusIcon';

export interface ExecutionNodeData extends DagNodeData {
  status?: WorkflowStepStatus;
  duration?: number;
  error?: string;
  selected?: boolean;
  currentIteration?: number;
  maxIterations?: number;
}

export type ExecutionFlowNode = Node<ExecutionNodeData>;

const STATUS_STYLES: Partial<Record<WorkflowStepStatus, string>> = {
  completed: 'border border-success/40 bg-[oklch(0.72_0.16_155/0.08)]',
  running:
    'border-2 border-accent-bright bg-[oklch(0.72_0.14_225/0.12)] shadow-[0_0_0_3px_oklch(0.72_0.14_225/0.15),0_4px_20px_oklch(0.72_0.14_225/0.25)]',
  failed: 'border border-error/40 bg-[oklch(0.65_0.21_28/0.08)]',
  skipped: 'border border-border bg-surface-elevated opacity-50',
};
const DEFAULT_STYLE = 'border border-border bg-surface-elevated';

const TYPE_COLORS: Record<string, string> = {
  command: 'text-[var(--node-prompt)]',
  prompt: 'text-accent-bright',
  bash: 'text-[var(--node-bash)]',
  loop: 'text-flame-hi',
};

const TYPE_LABELS: Record<string, string> = {
  command: 'CMD',
  bash: 'BASH',
  prompt: 'PROMPT',
  loop: 'LOOP',
};

function ExecutionDagNodeRender({ data }: NodeProps<ExecutionFlowNode>): React.ReactElement {
  const style = (data.status && STATUS_STYLES[data.status]) ?? DEFAULT_STYLE;
  const typeLabel = TYPE_LABELS[data.nodeType] ?? 'PROMPT';

  return (
    <div
      className={`min-w-[160px] rounded-lg px-3 py-2 transition-all duration-300 ${style}${data.selected ? ' ring-2 ring-accent-bright/40 ring-offset-2 ring-offset-background' : ''}`}
    >
      <Handle type="target" position={Position.Top} className="!bg-border !w-2 !h-2" />
      <div className="flex items-center gap-2">
        <StatusIcon status={data.status ?? 'pending'} />
        <span
          className={`font-mono text-[9.5px] font-bold uppercase tracking-widest ${TYPE_COLORS[data.nodeType] ?? 'text-text-tertiary'}`}
        >
          {typeLabel}
        </span>
        <span className="max-w-[100px] truncate font-mono text-xs font-semibold text-text-primary">
          {data.label}
        </span>
        {data.duration !== undefined && (
          <span className="text-[10px] text-text-tertiary ml-auto shrink-0">
            {formatDurationMs(data.duration)}
          </span>
        )}
      </div>
      {data.currentIteration !== undefined && data.maxIterations !== undefined && (
        <div className="text-[10px] text-text-tertiary mt-0.5">
          {data.currentIteration}/{data.maxIterations} iterations
        </div>
      )}
      {data.error && (
        <div className="text-[10px] text-error mt-1 truncate" title={data.error}>
          {data.error.slice(0, 60)}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="!bg-border !w-2 !h-2" />
    </div>
  );
}

export const executionDagNode = memo(ExecutionDagNodeRender);
